import React, { useState, useEffect, useCallback, useMemo, type JSX } from 'react';
import useFetchExportRequests from '@/features/agent/export_requests/api/useFetchExportRequests';
import useFetchExportRequestsDetails from '@/features/agent/export_requests/api/useFetchExportRequestsDetails';
import type { ExportRequest } from '@/features/agent/export_requests/types/ExportRequest';
import ExportItemSelectionModal from '@/features/agent/export_requests/components/ExportItemSelectionModal';
import { showErrorToast } from '@/features/agent/job_change_processes/lib/toast';
import { Button } from '@/components/Button';
import { MaterialIcon } from '@/components/MaterialIcon';
import { Select as SelectRoot, SelectContent, SelectItem } from '@/components/ui/select';
import * as SelectPrimitive from '@radix-ui/react-select';
import useFetchIam from '@/features/agent/api/useFetchIam';

const ArrowDropDownIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8.00039 11.1389L2.40039 5.53892H13.6004L8.00039 11.1389Z" fill="currentColor" />
  </svg>
);

const ExportRequestsPage: React.FC = () => {
  const { data: exportRequestsList, refetch } = useFetchExportRequests();
  const { data: iam } = useFetchIam();
  const isJobOnlyPlan = iam?.plan_type === 'job_only';

  const exportOptions = useMemo(
    () =>
      isJobOnlyPlan
        ? [
            { value: 'job_post', label: '求人データ' },
            { value: 'company', label: '企業データ' },
          ]
        : [
            { value: 'candidate_profile', label: '求職者データ' },
            { value: 'job_post', label: '求人データ' },
            { value: 'job_change_process', label: 'マッチングデータ' },
            { value: 'job_change_process_log', label: '選考ログデータ' },
            { value: 'company', label: '企業データ' },
            { value: 'candidate_interview_log', label: '面談情報データ' },
            { value: 'job_post_endpoint', label: '求人推薦データ' },
          ],
    [isJobOnlyPlan]
  );

  // モーダル/メニュー状態管理
  const [isCreateMenuOpen, setIsCreateMenuOpen] = useState(false);
  const [createMenuValue, setCreateMenuValue] = useState<string>('');
  const [isItemSelectionModalOpen, setIsItemSelectionModalOpen] = useState(false);
  const [selectedDataType, setSelectedDataType] = useState<string>('');

  // ダウンロード用の状態管理
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  // ダウンロード用の詳細データ取得
  const { data: downloadDetails } = useFetchExportRequestsDetails(downloadingId || '0', {
    enabled: !!downloadingId,
  });

  const exportRequests = exportRequestsList?.records || [];
  const isEmpty = exportRequests.length === 0;

  // ダウンロード詳細が取得された時の処理
  useEffect(() => {
    if (downloadDetails && downloadingId) {
      try {
        if (downloadDetails.file_url) {
          // ダウンロードURLがある場合、新しいタブでダウンロードを開始
          const link = document.createElement('a');
          link.href = downloadDetails.file_url;
          link.download = `${downloadDetails.resource_type_text}_${downloadDetails.created_at.replace(/[:\s]/g, '_')}.csv`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } else {
          showErrorToast('ダウンロード可能なファイルが見つかりません');
        }
      } catch (error) {
        console.error('Download failed:', error);
        showErrorToast('ダウンロードに失敗しました');
      } finally {
        // ダウンロード処理完了後にリセット
        setDownloadingId(null);
      }
    }
  }, [downloadDetails, downloadingId]);

  const handleItemSelectionModalClose = () => {
    setIsItemSelectionModalOpen(false);
    setSelectedDataType('');
  };

  const handleDataTypeSelected = (dataType: string) => {
    setSelectedDataType(dataType);
    setIsCreateMenuOpen(false);
    setIsItemSelectionModalOpen(true);
  };

  const handleExportSuccess = useCallback(
    (_actualExportId: number, _tempId: number) => {
      // POST成功時にリストを再取得
      refetch();
    },
    [refetch]
  );

  const handleDownload = (request: ExportRequest) => {
    // ダウンロード対象のIDを設定（これにより自動的にAPI呼び出しが開始される）
    setDownloadingId(request.id.toString());
  };

  const getDataTypeIcon = (resourceType: string): string => {
    switch (resourceType) {
      case 'job_post':
        return 'description'; // Material Symbol
      case 'candidate_profile':
        return 'person'; // Material Symbol
      case 'job_change_process':
        return 'handshake'; // Material Symbol
      case 'company':
        return 'domain'; // Material Symbol
      case 'candidate_interview_log':
        return 'event'; // Material Symbol
      case 'job_post_endpoint':
        return 'handshake'; // Material Symbol
      case 'job_change_process_log':
        return 'history'; // Material Symbol
      default:
        return 'description';
    }
  };

  // 文字コードのラベル表示
  const getEncodingLabel = (enc?: string): string => {
    switch (enc) {
      case 'utf8':
        return 'UTF-8（BOMなし）';
      case 'utf8_bom':
        return 'UTF-8（BOMつき）';
      case 'shift_jis':
        return 'Shift_JIS（Windows推奨）';
      default:
        return '-';
    }
  };

  const formatDateTime = (dateTimeString: string): string => {
    const date = new Date(dateTimeString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${year}/${month}/${day} ${hours}:${minutes}`;
  };

  const getStatusBadge = (request: ExportRequest): JSX.Element => {
    switch (request.status) {
      case 'failed':
        return (
          <span className="inline-flex h-6 items-center justify-center rounded-full bg-tag-red px-sm text-xs leading-none text-tag-red">
            失敗
          </span>
        );
      default:
        return (
          <span className="inline-flex h-6 items-center justify-center rounded-full bg-tag-gray px-sm text-xs leading-none text-main">
            期限切れ
          </span>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray px-lg pt-sm pb-lg">
      {/* 空状態 or エクスポート履歴テーブル */}
      {isEmpty ? (
        <div className="flex h-[90vh] flex-col items-center justify-center rounded-sm bg-white text-center shadow-sm">
          <div className="flex size-[120px] items-center justify-center">
            <img
              src="/agent/export_requests/csv_import.png"
              alt="export_requests_empty"
              className="size-full"
            />
          </div>
          <div className="mt-md text-lg leading-normal font-bold text-main">CSVでデータを出力</div>
          <div className="mt-md text-base text-main">
            {isJobOnlyPlan
              ? '企業・求人の情報をCSV形式でダウンロードできます。'
              : '企業・求人・求職者・マッチング情報をまとめてCSV形式でダウンロードできます。'}
          </div>
          <div className="mt-md">
            <SelectRoot
              open={isCreateMenuOpen}
              value={createMenuValue}
              onOpenChange={(open) => {
                setIsCreateMenuOpen(open);
                if (open) setCreateMenuValue('');
              }}
              onValueChange={(val) => {
                handleDataTypeSelected(val);
                setCreateMenuValue('');
              }}
            >
              <SelectPrimitive.Trigger asChild>
                <Button color="primary" size="md" className="inline-flex items-center">
                  データを出力する
                  <ArrowDropDownIcon />
                </Button>
              </SelectPrimitive.Trigger>
              <SelectContent>
                {exportOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </SelectRoot>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-md py-md">
          {/* データをエクスポートするボタン */}
          <div className="flex justify-end">
            <SelectRoot
              open={isCreateMenuOpen}
              value={createMenuValue}
              onOpenChange={(open) => {
                setIsCreateMenuOpen(open);
                if (open) setCreateMenuValue('');
              }}
              onValueChange={(val) => {
                handleDataTypeSelected(val);
                setCreateMenuValue('');
              }}
            >
              <SelectPrimitive.Trigger asChild>
                <Button color="primary" size="md" className="inline-flex items-center">
                  データを出力する
                  <ArrowDropDownIcon />
                </Button>
              </SelectPrimitive.Trigger>
              <SelectContent>
                {exportOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </SelectRoot>
          </div>
          <div className="overflow-x-auto rounded-sm border border-solid border-border bg-white shadow-layer1">
            <table
              className="w-full min-w-[1147px] table-fixed"
              style={{ borderCollapse: 'collapse' }}
            >
              <thead className="bg-gray_darken">
                <tr>
                  <th
                    className="w-[240px] px-md py-sm text-center text-base leading-normal font-bold text-main"
                    style={{
                      borderRight: '1px solid #D1D1D1',
                      borderBottom: '1px solid #D1D1D1',
                    }}
                  >
                    データの種類
                  </th>
                  <th
                    className="w-[240px] px-md py-sm text-center text-base leading-normal font-bold text-main"
                    style={{
                      borderBottom: '1px solid #D1D1D1',
                    }}
                  >
                    作成者
                  </th>
                  <th
                    className="w-[240px] px-md py-sm text-center text-base leading-normal font-bold text-main"
                    style={{
                      borderBottom: '1px solid #D1D1D1',
                    }}
                  >
                    文字コード設定
                  </th>
                  <th
                    className="w-[240px] px-md py-sm text-center text-base leading-normal font-bold text-main"
                    style={{
                      borderBottom: '1px solid #D1D1D1',
                    }}
                  >
                    生成日時
                  </th>
                  <th
                    className="w-[240px] px-md py-sm text-center text-base leading-normal font-bold text-main"
                    style={{
                      borderBottom: '1px solid #D1D1D1',
                    }}
                  >
                    ステータス
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white">
                {exportRequests.map((request) => (
                  <tr key={request.id}>
                    <td
                      className="h-12 w-[240px] align-middle whitespace-nowrap"
                      style={{
                        borderBottom: '1px solid #D1D1D1',
                      }}
                    >
                      <div className="flex h-12 items-center gap-xs pl-x3l">
                        <MaterialIcon
                          name={getDataTypeIcon(request.resource_type)}
                          filled
                          className="text-2xl text-primary"
                        />
                        <span className="text-sm font-bold text-main">
                          {request.resource_type_text}
                        </span>
                      </div>
                    </td>
                    <td
                      className="h-12 w-[240px] px-md text-center align-middle text-base whitespace-nowrap text-main"
                      style={{
                        borderBottom: '1px solid #D1D1D1',
                      }}
                    >
                      {request.organization_user?.name || '-'}
                    </td>
                    <td
                      className="h-12 w-[240px] px-md text-center align-middle text-base break-words text-main"
                      style={{
                        borderBottom: '1px solid #D1D1D1',
                      }}
                    >
                      {getEncodingLabel(request.encoding)}
                    </td>
                    <td
                      className="h-12 w-[240px] px-md text-center align-middle text-base whitespace-nowrap text-main"
                      style={{
                        borderBottom: '1px solid #D1D1D1',
                      }}
                    >
                      {formatDateTime(request.created_at)}
                    </td>
                    <td
                      className="h-12 w-[240px] px-md text-center align-middle whitespace-nowrap"
                      style={{
                        borderBottom: '1px solid #D1D1D1',
                      }}
                    >
                      <div className="flex items-center justify-center gap-lg">
                        {request.status === 'done' ? (
                          <button
                            onClick={() => handleDownload(request)}
                            className="inline-flex h-[42px] items-center gap-xxs rounded-sm px-sm text-sm font-bold text-primary hover:bg-primary_lighten"
                          >
                            <MaterialIcon name="download" filled className="text-xl" />
                            ダウンロード
                          </button>
                        ) : request.status === 'running' || request.status === 'ready' ? (
                          <MaterialIcon
                            name="progress_activity"
                            className="animate-spin text-2xl text-primary"
                          />
                        ) : (
                          getStatusBadge(request)
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 項目選択モーダル */}
      <ExportItemSelectionModal
        isOpen={isItemSelectionModalOpen}
        onClose={handleItemSelectionModalClose}
        onExport={handleExportSuccess}
        selectedDataType={selectedDataType}
      />
    </div>
  );
};

export default ExportRequestsPage;
